/**
 * CustomModelList Component
 * Displays brand's custom AI mannequin models in a grid layout
 * Supports model selection and deletion with confirmation
 */

import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Tag,
  Typography,
  Modal,
  Space,
  Empty,
  Tooltip,
  Badge,
} from 'antd';
import {
  DeleteOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { BrandCustomModel } from '../../services/onboardingService';

const { Text } = Typography;
const { confirm } = Modal;

interface CustomModelListProps {
  models: BrandCustomModel[];
  selectedModelId?: string | null;
  onSelect?: (modelId: string) => void;
  onDelete: (modelId: string) => Promise<void>;
  loading?: boolean;
  showDeleteButton?: boolean;
  compact?: boolean;
}

const CustomModelList: React.FC<CustomModelListProps> = ({
  models,
  selectedModelId,
  onSelect,
  onDelete,
  loading = false,
  showDeleteButton = true,
  compact = false,
}) => {
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = (model: BrandCustomModel) => {
    confirm({
      title: 'Modeli Sil',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            <strong>"{model.name}"</strong> modelini silmek istediğinize emin misiniz?
          </p>
          <Text type="secondary">
            Bu model ile oluşturulmuş içerikler etkilenmeyecektir.
          </Text>
        </div>
      ),
      okText: 'Sil',
      okType: 'danger',
      cancelText: 'İptal',
      onOk: async () => {
        try {
          setDeletingId(model.id);
          await onDelete(model.id);
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  if (models.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Henüz özel model oluşturulmamış"
      />
    );
  }

  const colSpan = compact ? { xs: 12, sm: 8, md: 6 } : { xs: 12, sm: 8, md: 6, lg: 4 };

  return (
    <Row gutter={[16, 16]}>
      {models.map((model) => (
        <Col {...colSpan} key={model.id}>
          <Badge.Ribbon
            text={model.creationType === 'ai_generated' ? 'AI' : 'Yüklendi'}
            color={model.creationType === 'ai_generated' ? 'purple' : 'blue'}
          >
            <Card
              hoverable={!!onSelect}
              className={`custom-model-card ${selectedModelId === model.id ? 'selected' : ''}`}
              onClick={() => onSelect?.(model.id)}
              style={{
                border: selectedModelId === model.id ? '2px solid #1890ff' : '1px solid #d9d9d9',
                borderRadius: 12,
                overflow: 'hidden',
                opacity: loading ? 0.6 : 1,
              }}
              bodyStyle={{ padding: compact ? 8 : 12, textAlign: 'center' }}
              cover={
                <div
                  style={{
                    position: 'relative',
                    paddingBottom: compact ? '120%' : '150%',
                    overflow: 'hidden',
                    background: '#f5f5f5',
                  }}
                >
                  <img
                    src={model.thumbnailUrl || model.imageUrl}
                    alt={model.name}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center',
                    }}
                  />
                  {selectedModelId === model.id && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        background: '#1890ff',
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckOutlined style={{ color: 'white', fontSize: 14 }} />
                    </div>
                  )}
                </div>
              }
              actions={
                showDeleteButton
                  ? [
                      <Tooltip title="Sil" key="delete">
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          loading={deletingId === model.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(model);
                          }}
                        />
                      </Tooltip>,
                    ]
                  : undefined
              }
            >
              <Text strong style={{ display: 'block', marginBottom: 4, fontSize: compact ? 12 : 14 }}>
                {model.name}
              </Text>
              <Space size={4}>
                <Tag color={model.gender === 'female' ? 'magenta' : 'blue'} style={{ fontSize: compact ? 10 : 12 }}>
                  {model.gender === 'female' ? 'Kadın' : 'Erkek'}
                </Tag>
              </Space>
            </Card>
          </Badge.Ribbon>
        </Col>
      ))}
    </Row>
  );
};

export default CustomModelList;
